// Keluaran dari proses Penyedia, on the Generator Dokumen page for a
// Penyedia (Simulasi Pluto v4 "Keluaran langsung dari 199 proses"): the
// Dokumen / Output Bukti of every supplier process step, or with a Paket
// aktif only the steps of its metode, jenis and kualifikasi (its checklist),
// each with Unduh Word, the step's skeleton filled from the paket at once.
//   toolbar   search and Tahap
//   groups    rows per stage: the output, the step's code, activity and group;
//             a decision step carries "Titik keputusan"
import { useState } from "react";
import * as API from "../api";
import { formatCount } from "../lib/format";
import { canDocx, useDocx } from "../pbj/docx";
import { useFeatures } from "../shell/features";
import { Badge, EmptyState, Notice, SearchInput, SectionHead, SkeletonRows, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";

function fold(text: string): string {
  return text.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
}

export function filterSupplierOutputs(list: API.SupplierOutput[], q: string, stage: string): API.SupplierOutput[] {
  const terms = fold(q).split(/\s+/).filter(Boolean);
  return list.filter((o) => {
    if (stage && o.stage !== stage) return false;
    const hay = fold([o.output, o.activity, o.group, o.code].join(" "));
    return terms.every((term) => hay.includes(term));
  });
}

export function SupplierOutputs({ projectId }: { projectId: string | null }) {
  const features = useFeatures();
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const list = useAsync(() => API.getSupplierOutputs(projectId ?? undefined), [projectId]);
  const docxOn = canDocx(features);
  const docx = useDocx();

  const data = list.data;
  const all = data?.outputs ?? [];
  const shown = filterSupplierOutputs(all, q, stage);
  const groups = (data?.stages ?? []).map((s) => ({ stage: s, items: shown.filter((o) => o.stage === s.id) })).filter((g) => g.items.length > 0);
  const lead = data?.project
    ? `${formatCount(all.length)} keluaran dari proses Penyedia untuk metode paket ${data.project.name}. Unduh Word langsung memakai data paketnya.`
    : `${formatCount(all.length)} keluaran dari basis data proses Penyedia. Pilih paket aktif agar daftarnya sesuai metode paket dan Word memakai datanya.`;

  return (
    <section className="region pbj-outputs" aria-labelledby="tp-sup-outputs-title">
      <SectionHead id="tp-sup-outputs-title" title="Keluaran dari proses Penyedia" lead={data ? lead : "Memuat keluaran"} />
      <div className="toolbar">
        <div className="field-row" data-cols="3">
          <div className="field">
            <label htmlFor="tp-sup-q">Cari keluaran</label>
            <SearchInput id="tp-sup-q" value={q} onChange={setQ} placeholder="Dokumen, kegiatan, atau kode" />
          </div>
          <div className="field">
            <label htmlFor="tp-sup-stage">Tahap</label>
            <select id="tp-sup-stage" value={stage} onChange={(e) => setStage(e.currentTarget.value)}>
              <option value="">Semua tahap</option>
              {(data?.stages ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {list.loading && !data ? (
        <SkeletonRows count={4} label="Memuat keluaran" />
      ) : list.error ? (
        <Notice tone="danger" title="Keluaran belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={list.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(list.error)}</p>
        </Notice>
      ) : groups.length === 0 ? (
        <EmptyState icon="filter" title="Tidak ada keluaran yang cocok" body="Ubah kata kunci atau pilih semua tahap." />
      ) : (
        <div className="kb-groups">
          {groups.map((g) => (
            <div key={g.stage.id} className="pbj-docs-head">
              <h3 className="pbj-subtitle">
                {g.stage.label} ({formatCount(g.items.length)})
              </h3>
              <ul className="rows">
                {g.items.map((o) => (
                  <li key={`${o.stage}:${o.code}`} className="row tp-row">
                    <span className="row-main">
                      <span className="row-title">{o.output}</span>
                      <span className="row-meta">
                        {o.code}. {o.activity}
                      </span>
                      <span className="row-meta">{o.group}</span>
                      {o.decision ? (
                        <span>
                          <Badge tone="warning">Titik keputusan</Badge>
                        </span>
                      ) : null}
                      {docx.error?.key === o.code ? (
                        <span className="pk-item-error" role="alert">
                          <Glyph name="danger" size={16} />
                          <span>{docx.error.message}</span>
                        </span>
                      ) : null}
                    </span>
                    {docxOn ? (
                      <span className="row-side">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={docx.busy !== null}
                          aria-busy={docx.busy === o.code || undefined}
                          aria-label={`Unduh Word ${o.output}`}
                          onClick={() => void docx.start(o.code, () => API.downloadStepDocx(o.code, projectId ?? undefined))}
                        >
                          <Glyph name="documentDownload" />
                          <span>{docx.busy === o.code ? "Menyiapkan" : "Word"}</span>
                        </button>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
